import { Tags } from "@/enum/tags";
import { Paginated } from "@/@types/paginated";
import { apiSlice } from "../http";
import {
  ExportTransactionsQuery,
  ImportResponse,
  ListTransactionsQuery,
  SummaryQuery,
  SummaryResponse,
  TransactionDetailResponse,
  TransactionListItem,
} from "./types";

export const cavOfflineSyncApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getTransactions: builder.query<
      Paginated<TransactionListItem>,
      ListTransactionsQuery
    >({
      query: (params) => {
        const urlParams = new URLSearchParams();

        if (params.page) urlParams.append("page", params.page.toString());
        if (params.limit) urlParams.append("limit", params.limit.toString());

        if (params.sortBy) urlParams.append("sortBy", params.sortBy);
        if (params.sortOrder) urlParams.append("sortOrder", params.sortOrder);

        if (params.status) {
          params.status.forEach((s) => urlParams.append("status", s));
        }
        if (params.storeIds) {
          params.storeIds.forEach((id) =>
            urlParams.append("storeIds", id.toString())
          );
        }
        if (params.dateFrom) urlParams.append("dateFrom", params.dateFrom);
        if (params.dateTo) urlParams.append("dateTo", params.dateTo);

        return {
          url: `/cav-offline-sync?${urlParams.toString()}`,
          method: "GET",
        };
      },
      providesTags: [Tags.CAV_OFFLINE_SYNC],
    }),

    getSummary: builder.query<SummaryResponse, SummaryQuery>({
      query: (params) => {
        const urlParams = new URLSearchParams();

        if (params.storeIds) {
          params.storeIds.forEach((id) =>
            urlParams.append("storeIds", id.toString())
          );
        }
        if (params.dateFrom) urlParams.append("dateFrom", params.dateFrom);
        if (params.dateTo) urlParams.append("dateTo", params.dateTo);
        if (params.status) {
          params.status.forEach((s) => urlParams.append("status", s));
        }

        return {
          url: `/cav-offline-sync/summary?${urlParams.toString()}`,
          method: "GET",
        };
      },
      providesTags: [Tags.CAV_OFFLINE_SYNC],
    }),

    exportTransactions: builder.query<
      TransactionListItem[],
      ExportTransactionsQuery
    >({
      query: (params) => {
        const urlParams = new URLSearchParams();

        if (params.sortBy) urlParams.append("sortBy", params.sortBy);
        if (params.sortOrder) urlParams.append("sortOrder", params.sortOrder);

        if (params.status) {
          params.status.forEach((s) => urlParams.append("status", s));
        }
        if (params.storeIds) {
          params.storeIds.forEach((id) =>
            urlParams.append("storeIds", id.toString())
          );
        }
        if (params.dateFrom) urlParams.append("dateFrom", params.dateFrom);
        if (params.dateTo) urlParams.append("dateTo", params.dateTo);

        return {
          url: `/cav-offline-sync/export?${urlParams.toString()}`,
          method: "GET",
        };
      },
    }),

    getTransactionDetail: builder.query<
      TransactionDetailResponse,
      { store: number; docCode: number }
    >({
      query: ({ store, docCode }) => ({
        url: `/cav-offline-sync/${store}/${docCode}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { store, docCode }) => [
        { type: Tags.CAV_OFFLINE_SYNC, id: `${store}-${docCode}` },
      ],
    }),

    importTransactions: builder.mutation<ImportResponse, File>({
      query: (file) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: "/cav-offline-sync/import",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: [Tags.CAV_OFFLINE_SYNC],
    }),
  }),
});

export const {
  useGetTransactionsQuery,
  useGetSummaryQuery,
  useGetTransactionDetailQuery,
  useImportTransactionsMutation,
} = cavOfflineSyncApi;
